import { config } from "@repo/config";

/**
 * Locales that are only partially translated: they must not be advertised via
 * hreflang / sitemap on pages that would just fall back to English, otherwise
 * we ship duplicate English content under a second URL.
 * Add a path here once that page has real copy in the locale.
 *
 * 空数组 = 该 locale 没有任何页面有真实译文，因此不广告任何 hreflang。
 */
export const PARTIAL_LOCALE_PATHS: Record<string, string[]> = {
	// 空数组 = 白名单为空 = 该 locale 一个路径都不广告。加一条路径就等于同时
	// 放开它的 hreflang 与 sitemap 条目，所以**译文没落地之前不要往里加** ——
	// deepmerge 缺 key 会静默回落英文，提前放行等于对外宣称有译文、实际给英文。
	"pt-BR": [
		"/", // criar vídeo com ia — br 33,100/月，哥飞 KD 32.4
		"/photo-to-cartoon", // transformar foto em desenho — br 18,100/月
		"/photo-to-anime", // transformar foto em anime — br 8,100/月
		"/ai-describe-image", // descrever imagem ia — br 210/月，KD 12
		"/image-to-prompt", // imagem para prompt — br 170/月，KD 12
		"/ai-edit-image", // ia para editar fotos — br 5,400/月；差异词 sem restrições KD 12.4
		"/image-to-video", // transformar foto em video — br 5,400/月，哥飞 KD 38.1
		"/grok-ai-video-generator", // ia que faz video — br 8,100/月，哥飞 KD 33.6
		"/ai-image-generator", // gerar imagem ia — br 14,800/月，哥飞 KD 33.4（首页占 gerador，这页用动词形）
		"/start-end-frame-video", // 无可验证搜索量；承接首页与 Seedance 页的葡语访客
		"/seedance-2-ai-video-generator", // Seedance 2.0 品牌词页已完成葡语翻译
		"/seedance-2-5-ai-video-generator", // Seedance 2.5 专页已完成葡语翻译
		"/tesla-roadster-ai-video-generator",
		"/templates/ai-clothes-changer", // trocar roupa ia — br 320/月；为承接现有访客而翻，不为词
		"/hotel-lobby-ai-video-generator",
		"/ai-kiss-video-generator",
		"/ai-french-kiss-video-generator",
		"/rumpelstiltskin-ai-video-generator",
	],
	es: [
		"/",
		"/tesla-roadster-ai-video-generator",
		"/grok-ai-video-generator",
		"/ai-edit-image", // editar fotos con ia — es 5,400/月，哥飞 KD 24.4
		"/ai-image-generator", // crear imágenes con ia — es 18,100/月，哥飞 KD 30.7
		"/photo-to-cartoon", // convertir foto en caricatura — es 480/月，哥飞 KD 30.4
		"/templates/ai-clothes-changer", // cambiar ropa con ia — es 390/月，哥飞 KD 21.6
		"/zombie-trend-ai-video-generator", // tendencia zombie con ia — no data；哥飞 KD 19.8
		"/bulin-47-ai-video-generator", // bulin 47 ia — ES no data（10 月新词）；Trends 西语写法约英文 40%；哥飞 KD 41
		"/ozil-goal-ai-video-generator", // Ozil IA — ES no data；哥飞 KD 33.6；西语工具页已完整翻译
		"/image-to-video",
		"/start-end-frame-video",
		"/seedance-2-ai-video-generator",
		"/seedance-2-5-ai-video-generator",
		"/hotel-lobby-ai-video-generator",
		"/ai-kiss-video-generator",
		"/ai-french-kiss-video-generator",
		"/rumpelstiltskin-ai-video-generator",
	],
};

/**
 * DEFAULT_LOCALE_ONLY_PATHS 里的页面默认拦住所有非英语 locale；这里记录已经
 * 单独完成翻译、但不能把整个 locale 切成白名单制的例外。
 */
const DEFAULT_LOCALE_ONLY_EXCEPTIONS: Record<string, string[]> = {
	"/ai-kiss-video-generator": ["zh", "zh-TW"],
	"/ozil-goal-ai-video-generator": ["zh", "zh-TW"],
	"/": ["zh", "ja"],
	"/start-end-frame-video": ["zh", "ja"],
	"/seedance-2-ai-video-generator": ["zh", "ja"],
	"/seedance-2-5-ai-video-generator": ["zh", "ja"],
	"/image-to-video": ["zh", "ja"],
};

/**
 * 只有英文文案的新页面：非默认语种一律不广告 hreflang，也不进 sitemap。
 * PARTIAL_LOCALE_PATHS 是「按 locale 开白名单」，一旦把 ja/zh/zh-TW 写进去，
 * 它们就从「全路径开放」翻转成「只开放名单内路径」，得把现有已翻译的页面全部
 * 枚举一遍才不误伤——所以这里改成按路径拦，只影响列出来的路径。
 *
 * 补齐译文后把对应路径从这里删掉即可。
 * 有白名单的 locale（pt-BR / es）不受这张表约束，只看自己的白名单——
 * 所以 pt-BR 单独译完某页时，加进 PARTIAL_LOCALE_PATHS 就行，不用动这里。
 */
const DEFAULT_LOCALE_ONLY_PATHS: string[] = [
	"/ai-music-generator",
	"/ai-music-video-generator",
	"/hop-hop-hop-dance-ai-video-generator",
	"/bye-bye-trend-ai-video-generator",
	"/transmission-5-dance-ai-video-generator",
	"/tiny-desk-ai-video-generator",
	"/flying-car-ai",
	"/tesla-roadster-ai-video-generator",
	// 首页已完成 es / zh / ja / pt-BR；通过 locale 白名单或上面的例外放行。
	// zh-TW 尚未完成，继续由这里拦住。
	"/",
	"/ai-outfit-describer",
	"/image-to-video",
	"/image-to-image",
	"/hotel-lobby-ai-video-generator",
	"/chuttamalle-ai-video-generator",
	"/zombie-trend-ai-video-generator",
	"/rumpelstiltskin-ai-video-generator",
	"/burning-bridges-ai-video-generator",
	"/bulin-47-ai-video-generator",
	"/ozil-goal-ai-video-generator",
	"/ai-kiss-video-generator",
	"/ai-french-kiss-video-generator",
	"/ai-wedding-video-generator",
	"/ai-hug-video-generator",
	"/ai-air-kiss-video-generator",
	"/ai-tongue-kiss-generator",
	"/ai-dance-video-generator",
	"/start-end-frame-video",
	"/reference-to-video",
	"/genjutsu-ai",
	"/seedance-2-ai-video-generator",
	"/seedance-2-5-ai-video-generator",
	"/kling-4-ai-video-generator",
	"/templates/image-to-emoji-converter",
	"/ai-muscle-generator",
	"/ai-big-butt",
	"/ai-feet-generator",
];

/**
 * Whether a locale should expose an alternate URL for the given page path.
 */
export function isLocaleAvailableForPath(
	locale: string,
	pagePath: string,
): boolean {
	const normalizedPath = pagePath || "/";
	// 有白名单的 locale 只看白名单（空数组也是 truthy，因此会挡住全部路径）。
	const allowedPaths = PARTIAL_LOCALE_PATHS[locale];
	if (allowedPaths) {
		return allowedPaths.includes(normalizedPath);
	}

	if (DEFAULT_LOCALE_ONLY_EXCEPTIONS[normalizedPath]?.includes(locale)) {
		return true;
	}

	// 没有白名单的 locale（ja / zh / zh-TW）默认全开，只被这张表按路径拦下。
	return !(
		locale !== config.i18n.defaultLocale &&
		DEFAULT_LOCALE_ONLY_PATHS.includes(normalizedPath)
	);
}

/**
 * Generate alternate language URLs for hreflang tags.
 * Used in page metadata to help search engines understand language variants.
 */
export function generateAlternateLanguages(
	pagePath: string,
	baseUrl: string,
): Record<string, string> {
	const languages = Object.keys(config.i18n.locales).reduce(
		(acc, lang) => {
			if (!isLocaleAvailableForPath(lang, pagePath)) {
				return acc;
			}
			const altPath =
				lang === config.i18n.defaultLocale
					? pagePath
					: `/${lang}${pagePath}`;
			acc[lang] = new URL(altPath, baseUrl).toString();
			return acc;
		},
		{} as Record<string, string>,
	);

	// x-default 指向默认语种，告诉 Google「语种没匹配上时给这个」。少了它，
	// 未覆盖语种的用户会被随机分到某个具体语种版本。
	// 默认语种本身被 PARTIAL_LOCALE_PATHS 挡掉时不输出，否则会指向一个不存在的页。
	if (languages[config.i18n.defaultLocale]) {
		languages["x-default"] = languages[config.i18n.defaultLocale];
	}

	return languages;
}
